/**
 * Gets the published date for a blog post.
 * Primary: Uses data.published_date (custom field set by content editors)
 * Fallback: Uses Prismic's last_publication_date
 *
 * This ensures that the displayed date reflects when the article was originally
 * published, not when minor updates were made to non-relevant fields.
 *
 * @param blog - The blog document
 * @returns The published date as a string, or undefined if no date is available
 */
export function getBlogPublishedDate(blog: {
  data?: { published_date?: string | null };
  last_publication_date?: string | null;
  first_publication_date?: string | null;
}): string | undefined {
  // Priority 1: Custom published_date field
  if (blog.data?.published_date) {
    return blog.data.published_date;
  }

  // Priority 2: Prismic's last_publication_date
  if (blog.last_publication_date) {
    return blog.last_publication_date;
  }

  // Priority 3: Prismic's first_publication_date (rare fallback)
  if (blog.first_publication_date) {
    return blog.first_publication_date;
  }

  return undefined;
}

/**
 * Gets the published date for a blog post from its data object directly
 * Useful when you only have the data portion of a blog document
 */
export function getBlogPublishedDateFromData(
  data: { published_date?: string | null },
  fallbackDate?: string | null,
): string | undefined {
  return data.published_date || fallbackDate || undefined;
}

/**
 * Sorts blog documents by their published date in descending order (newest first)
 * Uses the same fallback logic as getBlogPublishedDate
 */
export function sortBlogsByPublishedDate<
  T extends {
    data?: { published_date?: string | null };
    last_publication_date?: string | null;
    first_publication_date?: string | null;
  },
>(blogs: T[]): T[] {
  return [...blogs].sort((a, b) => {
    const dateA = getBlogPublishedDate(a);
    const dateB = getBlogPublishedDate(b);

    // Handle missing dates
    if (!dateA && !dateB) return 0;
    if (!dateA) return 1; // Push items without dates to the end
    if (!dateB) return -1;

    // Sort descending (newest first)
    return new Date(dateB).getTime() - new Date(dateA).getTime();
  });
}
